import React, { useEffect, useState } from 'react';
import { Button } from 'semantic-ui-react';
import { useComponentConfigClient } from './componentConfig.client';
import { useFluidFormConfigClient } from '../formBuilder/fluidFormConfig.client';
// ─── Helpers ──────────────────────────────────────────────────────────────────
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
const inputClass = 'w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400';
const retentionOptions = [
    { value: 'session', label: 'Session only' },
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
    { value: 'forever', label: 'Forever' },
];
const toneOptions = [
    { value: 'professional', label: 'Professional' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'concise', label: 'Concise' },
    { value: 'technical', label: 'Technical' },
];
function buildDefaultSettings(component) {
    return { ...(component.settings || {}) };
}
const SettingsPanel = ({ componentType, settings, forms, onChange }) => {
    const s = settings;
    const isChatbot = componentType === 'chatbot';
    const hasFileSizeField = componentType !== 'chatbot';
    const set = (key, value) => onChange({ ...settings, [key]: value });
    return (React.createElement("div", { className: "space-y-6" },
        React.createElement("div", { className: "bg-white border border-gray-200 rounded-lg p-5 space-y-4" },
            React.createElement("h4", { className: "font-semibold text-gray-800 text-base" }, "Schema Enforcement"),
            React.createElement("div", { className: "flex items-center gap-3" },
                React.createElement("input", { type: "checkbox", id: "enforceSchema", checked: s.enforceSchema ?? false, onChange: e => set('enforceSchema', e.target.checked), className: "w-4 h-4 accent-orange-500 cursor-pointer" }),
                React.createElement("label", { htmlFor: "enforceSchema", className: "text-sm font-medium text-gray-700 cursor-pointer select-none" }, "Enforce Schema \u2014 validate and structure output against a FluidForm")),
            React.createElement("div", null,
                React.createElement("label", { className: labelClass }, "Schema Form"),
                React.createElement("select", { className: inputClass, value: s.schemaFormKey ?? '', disabled: !s.enforceSchema, onChange: e => set('schemaFormKey', e.target.value) },
                    React.createElement("option", { value: "" }, s.enforceSchema ? 'Select a form...' : 'Enable "Enforce Schema" to select a form'),
                    forms.map(f => (React.createElement("option", { key: f.formKey, value: f.formKey }, f.name || f.formKey)))),
                s.enforceSchema && (React.createElement("p", { className: "text-xs text-gray-400 mt-1" }, "The agent will map its output to this form's field structure.")))),
        React.createElement("div", { className: "bg-white border border-gray-200 rounded-lg p-5 space-y-4" },
            React.createElement("h4", { className: "font-semibold text-gray-800 text-base" }, "General"),
            hasFileSizeField && (React.createElement("div", null,
                React.createElement("label", { className: labelClass }, "Max File Size (MB)"),
                React.createElement("input", { type: "number", min: 1, className: inputClass, value: s.maxFileSizeMb ?? '', placeholder: "No limit", onChange: e => set('maxFileSizeMb', e.target.value ? Number(e.target.value) : undefined) }))),
            React.createElement("div", null,
                React.createElement("label", { className: labelClass }, "Additional Context"),
                React.createElement("textarea", { rows: 3, className: inputClass + ' resize-none', value: s.additionalContext ?? '', placeholder: "Extra instructions injected into the agent's system prompt...", onChange: e => set('additionalContext', e.target.value || undefined) }),
                React.createElement("p", { className: "text-xs text-gray-400 mt-1" }, "Injected as system context into every agent call.")),
            React.createElement("div", null,
                React.createElement("label", { className: labelClass }, "Retention Policy"),
                React.createElement("select", { className: inputClass, value: s.retentionPolicy ?? '', onChange: e => set('retentionPolicy', e.target.value || undefined) },
                    React.createElement("option", { value: "" }, "Default"),
                    retentionOptions.map(o => (React.createElement("option", { key: o.value, value: o.value }, o.label)))),
                React.createElement("p", { className: "text-xs text-gray-400 mt-1" }, "How long interaction logs and files are retained.")),
            isChatbot && (React.createElement(React.Fragment, null,
                React.createElement("div", null,
                    React.createElement("label", { className: labelClass }, "Agent Tone"),
                    React.createElement("select", { className: inputClass, value: s.agentTone ?? '', onChange: e => set('agentTone', e.target.value || undefined) },
                        React.createElement("option", { value: "" }, "Default"),
                        toneOptions.map(o => (React.createElement("option", { key: o.value, value: o.value }, o.label))))),
                React.createElement("div", null,
                    React.createElement("label", { className: labelClass }, "Max Turns"),
                    React.createElement("input", { type: "number", min: 1, className: inputClass, value: s.maxTurns ?? '', placeholder: "No limit", onChange: e => set('maxTurns', e.target.value ? Number(e.target.value) : undefined) }),
                    React.createElement("p", { className: "text-xs text-gray-400 mt-1" }, "Conversation resets after this many turns.")))))));
};
const ConfigurationTab = ({ component, onComponentUpdated }) => {
    const client = useComponentConfigClient();
    const formClient = useFluidFormConfigClient();
    const [forms, setForms] = useState([]);
    const [settings, setSettings] = useState(() => buildDefaultSettings(component));
    const [saveState, setSaveState] = useState('idle');
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        setSettings(buildDefaultSettings(component));
    }, [component.componentId]);
    useEffect(() => {
        formClient.listFormConfigs(false).then(setForms).catch(() => { });
    }, []);
    const handleCopy = () => {
        navigator.clipboard.writeText(component.componentId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    const handleSave = async () => {
        setSaveState('saving');
        try {
            const updated = await client.updateSettings(component.componentId, settings);
            onComponentUpdated(updated);
            setSaveState('saved');
            setTimeout(() => setSaveState('idle'), 2500);
        }
        catch {
            setSaveState('error');
            setTimeout(() => setSaveState('idle'), 3000);
        }
    };
    return (React.createElement("div", { className: "space-y-6" },
        React.createElement("div", { className: "bg-white border border-gray-200 rounded-lg p-5" },
            React.createElement("h4", { className: "font-semibold text-gray-800 text-base mb-2" }, "Component ID"),
            React.createElement("div", { className: "flex items-center gap-3" },
                React.createElement("code", { className: "flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono text-gray-800" }, component.componentId),
                React.createElement("button", { onClick: handleCopy, className: "px-3 py-2 text-sm border border-gray-200 rounded hover:bg-gray-50 transition-colors whitespace-nowrap" }, copied ? '✓ Copied' : 'Copy')),
            React.createElement("p", { className: "text-xs text-gray-500 mt-2" },
                "Pass this as ",
                React.createElement("code", { className: "bg-gray-100 px-1 rounded" }, "componentId"),
                " in your component props or as a query parameter (",
                React.createElement("code", { className: "bg-gray-100 px-1 rounded" },
                    "?componentId=",
                    component.componentId),
                ") to enable monitoring.")),
        React.createElement("div", null,
            React.createElement("div", { className: "flex items-center justify-between mb-3" },
                React.createElement("h4", { className: "font-semibold text-gray-800 text-base" }, "Settings"),
                React.createElement("div", { className: "flex items-center gap-3" },
                    saveState === 'saved' && (React.createElement("span", { className: "text-sm text-green-600" }, "\u2713 Saved")),
                    saveState === 'error' && (React.createElement("span", { className: "text-sm text-red-500" }, "Failed to save")),
                    React.createElement(Button, { primary: true, size: "small", loading: saveState === 'saving', disabled: saveState === 'saving', onClick: handleSave }, "Save Settings"))),
            React.createElement(SettingsPanel, { componentType: component.componentType, settings: settings, forms: forms, onChange: setSettings }))));
};
export default ConfigurationTab;
